import { styled } from '@mui/material/styles';
import { COLOR } from '@src/styles/color';

export const StyledExportPanel = styled('div')`
  box-sizing: border-box;
  width: 400px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${COLOR.white};
  border-radius: 16px;
  box-shadow: 0px 4px 16px 0px #00000014;

  .export-panel-tabs {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .export-panel-tab {
    box-sizing: border-box;
    height: 40px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    /* body sets -0.4px globally; tab labels are tracked at 0 */
    letter-spacing: 0;
    color: ${COLOR.label2[64]};

    &.active {
      border-bottom-color: ${COLOR.darkBlue};
      color: ${COLOR.darkGray};
    }
  }

  /* Each export form renders its own StyledDownloadMenu card. Inside the panel
     the card surface belongs to the panel, so the nested one is flattened here
     instead of editing all four forms. */
  .export-panel-form > * {
    width: 100%;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const StyledExportHistoryList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* five 64px rows and their 16px gaps, the list height in the design */
  max-height: 384px;
  overflow-y: auto;
  /* The scrollbar sits on the panel's right edge, 8px clear of the rows. The
     negative margin spends the panel's 16px right padding on the list, then
     8px of it comes back as padding so the rows keep their designed 368px and
     the scrollbar takes the last 8px. */
  margin-right: -16px;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${COLOR.white};
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLOR.tooltipColor};
    border-radius: 100px;
  }

  .load-more-loading {
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiCircularProgress-root {
      color: ${COLOR.blueV2};
    }
  }
`;

export const StyledExportHistoryLoading = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  /* same footprint as the empty state so the popover does not resize when the
     first page arrives */
  min-height: 214px;

  .MuiCircularProgress-root {
    color: ${COLOR.blueV2};
  }
`;

export const StyledExportHistoryItem = styled('div')`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 64px;
  /* the list scrolls, so rows must keep their height instead of being squeezed
     by the flex container */
  flex-shrink: 0;
  padding: 8px 0;

  .file-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 8px;
  }

  .file-icon img {
    display: block;
    width: 32px;
    height: 32px;
  }

  .info {
    flex: 1 0 0;
    /* lets the file name shrink to an ellipsis instead of pushing the action */
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
  }

  .file-name {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    /* body sets -0.4px globally; the file name is tracked at 0 */
    letter-spacing: 0;
    color: ${COLOR.darkGray};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .details {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .detail-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.4px;
    color: ${COLOR.label2[64]};
    white-space: nowrap;
  }

  .detail-text.failed {
    color: ${COLOR.functionalRed.default};
  }

  .dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: ${COLOR.indigo[16]};
    }
  }

  .status-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pending-icon {
    color: ${COLOR.blueV2};
  }

  .failed-icon {
    font-size: 24px;
    color: ${COLOR.functionalRed.default};
  }

  .download-wrap {
    flex-shrink: 0;
    display: flex;
  }

  .download-btn {
    box-sizing: border-box;
    width: 40px;
    height: 40px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${COLOR.white};
    border: 1px solid ${COLOR.indigo[16]};
    border-radius: 8px;
    cursor: pointer;

    svg {
      font-size: 24px;
      color: ${COLOR.indigo[80]};
    }

    &:hover {
      background-color: ${COLOR.indigo[4]};
    }

    /* The button is left enabled while its file is being fetched: the disabled
       state fades it to the opacity below and drops the hover event the tooltip
       needs. What it stops answering, it stops offering. */
    &.downloading {
      cursor: default;

      &:hover {
        background-color: ${COLOR.white};
      }
    }

    &:disabled {
      background-color: ${COLOR.white};
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
`;

export const StyledExportHistoryEmpty = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;

  /* The design frames the illustration in a 179x102 window (node 2571:17016). */
  .empty-illustration {
    width: 179px;
    height: 102px;
    overflow: hidden;
    display: flex;
    justify-content: center;
  }

  .empty-illustration img {
    width: 150px;
    height: 120px;
    /* The shared asset pads 20px of transparent space above the artwork while
       the design window keeps 2px of it, so pull the other 18px up. That lands
       the artwork 2px below the top of the window and 6px above its bottom,
       matching the design. */
    margin-top: -18px;
  }

  .empty-message {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.4px;
    text-align: center;
    color: ${COLOR.label2[64]};
  }
`;
